import Vue from "vue";
import VueI18n from "vue-i18n";

import customEN from "@/locales/lang/en";
import enLocale from "element-ui/lib/locale/lang/en";
import { localeMap } from "@/components/company-common-components/constants";
import { reloadOnceForChunkError } from "@/utils/chunk-reload";

Vue.use(VueI18n);

const DEFAULT_LOCALE = "en";
const STARTUP_LOCALE_TIMEOUT_MS = 8000;

const localeLoaders = {
  "zh-cn": () =>
    Promise.all([
      import(
        /* webpackChunkName: "locale-zh-cn" */ "@/locales/lang/zh-cn"
      ),
      import(
        /* webpackChunkName: "locale-zh-cn" */ "element-ui/lib/locale/lang/zh-CN"
      ),
    ]),
  "zh-tw": () =>
    Promise.all([
      import(
        /* webpackChunkName: "locale-zh-tw" */ "@/locales/lang/zh-tw"
      ),
      import(
        /* webpackChunkName: "locale-zh-tw" */ "element-ui/lib/locale/lang/zh-TW"
      ),
    ]),
  ja: () =>
    Promise.all([
      import(/* webpackChunkName: "locale-ja" */ "@/locales/lang/ja"),
      import(
        /* webpackChunkName: "locale-ja" */ "element-ui/lib/locale/lang/ja"
      ),
    ]),
  hi: () =>
    Promise.all([
      import(/* webpackChunkName: "locale-hi" */ "@/locales/lang/hi"),
    ]),
  ko: () =>
    Promise.all([
      import(/* webpackChunkName: "locale-ko" */ "@/locales/lang/ko"),
      import(
        /* webpackChunkName: "locale-ko" */ "element-ui/lib/locale/lang/ko"
      ),
    ]),
};

const i18n = new VueI18n({
  locale: DEFAULT_LOCALE,
  fallbackLocale: DEFAULT_LOCALE,
  silentFallbackWarn: true,
  messages: {
    [DEFAULT_LOCALE]: {
      ...enLocale,
      ...customEN,
    },
  },
});

const loadedLocales = new Set([DEFAULT_LOCALE]);
let latestLocaleRequestId = 0;

export function getStoredLanguage() {
  try {
    const stored = localStorage.getItem("language");
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    return null;
  }
}

export function getStoredLocale() {
  return localeMap.get(getStoredLanguage()?.code) || DEFAULT_LOCALE;
}

async function loadAndActivateLocale(
  locale,
  { reloadOnChunkError },
  requestId,
) {
  const nextLocale = localeLoaders[locale] ? locale : DEFAULT_LOCALE;

  if (!loadedLocales.has(nextLocale)) {
    let customModule;
    let elementModule;
    try {
      [customModule, elementModule] = await localeLoaders[nextLocale]();
    } catch (error) {
      if (reloadOnChunkError && requestId === latestLocaleRequestId) {
        reloadOnceForChunkError(error);
      }
      throw error;
    }
    i18n.setLocaleMessage(nextLocale, {
      ...(elementModule?.default || {}),
      ...customModule.default,
    });
    loadedLocales.add(nextLocale);
  }

  if (requestId === latestLocaleRequestId) {
    i18n.locale = nextLocale;
  }
  return i18n.locale;
}

export async function setI18nLocale(locale, { reloadOnChunkError = true } = {}) {
  const requestId = ++latestLocaleRequestId;
  return loadAndActivateLocale(locale, { reloadOnChunkError }, requestId);
}

export async function loadStoredLocale() {
  const requestId = ++latestLocaleRequestId;
  let timeoutId;
  const activation = loadAndActivateLocale(
    getStoredLocale(),
    { reloadOnChunkError: false },
    requestId,
  );
  const timeout = new Promise((resolve) => {
    timeoutId = setTimeout(() => {
      if (requestId === latestLocaleRequestId) {
        latestLocaleRequestId += 1;
        i18n.locale = DEFAULT_LOCALE;
        resolve(DEFAULT_LOCALE);
        return;
      }
      resolve(i18n.locale);
    }, STARTUP_LOCALE_TIMEOUT_MS);
  });

  try {
    return await Promise.race([activation, timeout]);
  } catch (error) {
    if (requestId === latestLocaleRequestId) {
      i18n.locale = DEFAULT_LOCALE;
    }
    return i18n.locale;
  } finally {
    clearTimeout(timeoutId);
  }
}

export default i18n;
